import React from 'react';
import { CalendarDays, ArrowRight, Star, Trophy } from 'lucide-react';
import { siteConfig, freelancerStats } from '../data/mock';
import EmailComposer from './EmailComposer';
import { Avatar } from './Brand';

const HeroSection = () => {
  const go = (e, href) => {
    e.preventDefault();
    document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative pt-[120px] lg:pt-[150px] pb-20 lg:pb-28 overflow-hidden grid-paper" data-testid="hero-section">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#FAFAF7]" />
      <div className="absolute -top-32 right-[10%] w-[480px] h-[480px] rounded-full bg-[#10B981]/10 blur-[120px]" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-14 lg:gap-10 items-center">
          <div className="lg:col-span-6">
            <a
              href={siteConfig.freelancerProfile}
              target="_blank"
              rel="noopener noreferrer"
              className="fade-up inline-flex items-center gap-3 pl-1.5 pr-4 py-1.5 rounded-full bg-white border border-[#E2E8F0] shadow-sm mb-8 hover:border-[#10B981] transition-colors"
              data-testid="hero-top3-badge"
            >
              <span className="w-8 h-8 rounded-full bg-ink flex items-center justify-center"><Trophy className="w-4 h-4 text-[#10B981]" /></span>
              <span className="text-sm font-medium text-ink">
                Preferred Freelancer · <span className="font-bold">{freelancerStats.rank}</span> {freelancerStats.rankOf} on Freelancer.com
              </span>
            </a>

            <h1
              className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-[3.9rem] font-extrabold text-ink leading-[1.03] mb-7 fade-up"
              style={{ animationDelay: '100ms' }}
              data-testid="hero-headline"
            >
              Cold email that reaches the <span className="mark">primary inbox</span> — and gets a reply.
            </h1>

            <p className="text-base sm:text-lg text-slate max-w-xl leading-relaxed mb-9 fade-up" style={{ animationDelay: '180ms' }} data-testid="hero-subtitle">
              I'm <strong className="text-ink font-semibold">{siteConfig.personName}</strong>. I build the email infrastructure,
              find and verify the right B2B leads, and write the sequences — so your outbound books meetings instead of
              landing in spam.
            </p>

            <div className="flex flex-wrap items-center gap-3 mb-10 fade-up" style={{ animationDelay: '260ms' }}>
              <a href={siteConfig.calendly} target="_blank" rel="noopener noreferrer" className="btn-green" data-testid="hero-book-call-button">
                <CalendarDays className="w-4 h-4" /> Book a 30-min call
              </a>
              <a href="#services" onClick={(e) => go(e, '#services')} className="btn-outline" data-testid="hero-see-services-button">
                See what I do <ArrowRight className="w-4 h-4" />
              </a>
            </div>

            <div className="flex flex-wrap items-center gap-x-6 gap-y-3 fade-up" style={{ animationDelay: '340ms' }} data-testid="hero-proof">
              <div className="flex items-center gap-2.5">
                <Avatar size="w-10 h-10" />
                <div className="leading-tight">
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-[#F59E0B] text-[#F59E0B]" />)}
                    <span className="text-sm font-bold text-ink ml-1">{freelancerStats.rating}</span>
                  </div>
                  <p className="text-xs text-slate-2">{freelancerStats.reviews} client reviews · {freelancerStats.onTime} on time</p>
                </div>
              </div>
              <span className="hidden sm:block w-px h-8 bg-[#E2E8F0]" />
              <p className="text-sm text-slate">
                <span className="font-semibold text-ink">{freelancerStats.years} years</span> · Upwork Top Rated Plus · Fiverr Top Rated
              </p>
            </div>
          </div>

          <div className="lg:col-span-6 lg:pl-6 pt-6 lg:pt-0">
            <EmailComposer />
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
